import React, { useEffect, useState, useCallback } from "react";
import { Box, Button } from "@mui/material";
import RowAction, { RowActions } from "../ui/RowAction";
import { IconPen, IconPlus } from "../ui/icons";
import { useNavigate } from "react-router-dom";
import supabase from "../../supabase";
import { ErrorModal } from "../ModalAlert";
import DataTable from "../ui/DataTable";
import { colors } from "../../constants/colors";
import { LoaderSpinner } from "../spinner/loader";
import { useIsMobile } from "../../hooks/useIsMobile";

const ClientsTable = ({ setOpen, setFormState, formState }) => {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sortDirection, setSortDirection] = useState("asc");
  const [sortedClients, setSortedClients] = useState([]);
  const [displayedClients, setDisplayedClients] = useState([]);
  const [searchText, setSearchText] = useState("");
  const isMobile = useIsMobile();

  const getClients = useCallback(async () => {
    setLoading(true);
    try {
      const { data: clientsData, error } = await supabase
        .from("clients")
        .select("*")
        .order('company', { ascending: true });
      
      if (error) {
        throw new Error(error.message);
      }
      setClients(clientsData);
    } catch (error) {
      ErrorModal({ title: error.message });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    getClients();
  }, [getClients]);

  // Ordenar clientes cuando cambia la dirección de ordenamiento o los clientes
  useEffect(() => {
    if (clients.length > 0) {
      const clientsWithSortField = clients.map(client => ({
        ...client,
        sortField: `${client.company || ""} ${client.name || ""} ${client.lastName || ""}`
      }));
      
      const sorted = [...clientsWithSortField].sort((a, b) => {
        if (sortDirection === "asc") {
          return a.sortField.localeCompare(b.sortField);
        } else {
          return b.sortField.localeCompare(a.sortField);
        }
      });
      
      setSortedClients(sorted);
      setDisplayedClients(sorted);
    }
  }, [clients, sortDirection]);

  // Función para filtrar clientes según el texto de búsqueda
  const filterClients = (text) => {
    if (!text) {
      setDisplayedClients(sortedClients);
      return;
    }

    const lowerText = text.toLowerCase();
    const filtered = sortedClients.filter(client => {
      // Buscar en el ID del cliente
      if (client.clientId?.toString().includes(lowerText)) return true;
      
      // Buscar en el nombre de la empresa
      if (client.company?.toLowerCase().includes(lowerText)) return true;
      
      // Buscar en el nombre
      if (client.name?.toLowerCase().includes(lowerText)) return true;
      
      // Buscar en el apellido
      if (client.lastName?.toLowerCase().includes(lowerText)) return true;
      
      // Buscar en el email
      if (client.email?.toLowerCase().includes(lowerText)) return true;
      
      // Buscar en el teléfono
      if (client.phone?.toLowerCase().includes(lowerText)) return true;
      
      return false;
    });
    
    setDisplayedClients(filtered);
  };

  // La tabla se usa en dos contextos: el listado de /clients (sin props) y el
  // modal de selección dentro de una orden. Elegir fila sólo aplica al modal.
  const isPicker = typeof setFormState === "function";

  const handleRowClick = (rowData, rowMeta) => {
    if (!isPicker) return;
    const dataIndex = rowMeta.dataIndex;
    setFormState((prevState) => ({
      ...prevState,
      client: displayedClients[dataIndex],
    }));
    if (typeof setOpen === "function") setOpen(false);
  };

  const renderActions = (dataIndex) => {
    const item = displayedClients[dataIndex];
    return (
      <RowActions>
        <RowAction
          icon={IconPen}
          title="Editar cliente"
          onClick={() => navigate("/edit-client", { state: { client: item } })}
        />
      </RowActions>
    );
  };

  // Manejador de eventos para el encabezado
  const handleSortChange = () => {
    setSortDirection(sortDirection === "asc" ? "desc" : "asc");
  };

  // Columnas adaptadas para móvil
  const mobileColumns = [
    {
      name: "sortField",
      label: (
        <div 
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
          onClick={handleSortChange}
        >
          Cliente
          <span style={{ fontSize: '0.8rem', marginLeft: '5px' }}>
            {sortDirection === "asc" ? "↑" : "↓"}
          </span>
        </div>
      ),
      options: { 
        filter: false, 
        sort: false,
        customBodyRender: (value, tableMeta) => {
          const dataIndex = tableMeta.rowIndex;
          const client = displayedClients[dataIndex];
          return (
            <div style={{ padding: '8px 0' }}>
              <div style={{ fontWeight: 'bold' }}>{client.company || "Sin empresa"}</div>
              <div style={{ fontSize: '0.9rem' }}>
                {client.name || ""} {client.lastName || ""}
              </div>
            </div>
          );
        }
      },
    },
    {
      name: "actions",
      label: "",
      options: {
        filter: false,
        sort: false,
        customBodyRenderLite: renderActions,
      },
    },
  ];

  // Columnas para escritorio (las existentes)
  const desktopColumns = [
    { name: "clientId", label: "ID", options: { filter: false, sort: true } },
    {
      name: "company",
      label: "Empresa",
      options: { filter: false, sort: true },
    },
    { name: "name", label: "Nombre", options: { filter: false, sort: true } },
    {
      name: "lastName",
      label: "Apellido",
      options: { filter: false, sort: true },
    },
    { name: "email", label: "Email", options: { filter: false, sort: true } },
    {
      name: "actions",
      label: "Acciones",
      options: {
        filter: false,
        sort: false,
        customBodyRenderLite: renderActions,
      },
    },
  ];

  const columns = isMobile ? mobileColumns : desktopColumns;

  const options = {
    responsive: isMobile ? "standard" : "simple",
    sort: false,
    selectableRows: formState?.client ? "single" : "none",
    onRowSelectionChange: (currentRowsSelected) => {
      if (!isPicker) return;
      if (currentRowsSelected && currentRowsSelected.length > 0) {
        setFormState((prevState) => ({
          ...prevState,
          client: displayedClients[currentRowsSelected[0].dataIndex],
        }));
        if (typeof setOpen === "function") setOpen(false);
      }
    },
    selectableRowsOnClick: true,
    onRowClick: handleRowClick,
    filter: !isMobile,
    download: !isMobile,
    print: !isMobile,
    viewColumns: !isMobile,
    pagination: true,
    rowsPerPage: isMobile ? 10 : 15,
    rowsPerPageOptions: isMobile ? [10, 20, 50] : [15, 30, 50, 100],
    sortFilterList: false,
    enableNestedDataAccess: ".",
    downloadOptions: {
      filename: "clientes.csv",
      separator: ";",
      filterOptions: { useDisplayedColumnsOnly: true, useDisplayedRowsOnly: true },
    },
  };

  return loading ? (
    <Box
      sx={{
        width: "100%",
        height: "200px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <LoaderSpinner />
    </Box>
  ) : (
    <DataTable
      data={displayedClients}
      columns={columns}
      search={{
        value: searchText,
        onChange: (value) => {
          setSearchText(value || "");
          filterClients(value);
        },
        placeholder: "Buscar por empresa, nombre o CUIT…",
      }}
      actions={
        !isPicker && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<IconPlus size={15} />}
            onClick={() => navigate("/edit-client")}
            sx={{ minHeight: 36, whiteSpace: "nowrap" }}
          >
            Agregar cliente
          </Button>
        )
      }
      resultCount={displayedClients.length}
      totalCount={displayedClients.length}
      emptyMessage="No se encontraron clientes"
      options={options}
    />
  );
};

export default ClientsTable;
